export class ColorGenerator {
  /**
   * An array of colors to use for the linecharts
   */
  public static availableColors: string[] = [];
  /**
   *
   */
  public static readonly CONTRAST_OFFSET = 0.05;
  /**
   *
   * @returns
   */
  public static setDefault() {
    ColorGenerator.availableColors = ['#EC407A', '#6A8186', '#923C3B', '#A6978B', '#2196F3', '#795548', '#512DA8', '#56B496', '#854B85', '#1D9BB2'];
  }
  /**
   *
   */
  static readonly usedColors: string[] = [];

  /**
   * Getter for a single color from the static colors array
   */
  public static getColor() {
    if (ColorGenerator.availableColors.length <= 0) {
      ColorGenerator.generateColors(10);
    }

    const color = ColorGenerator.availableColors.pop();

    return color;
  }

  /**
   *
   */
  constructor() {
    ColorGenerator.setDefault();

    ColorGenerator.availableColors = ColorGenerator.availableColors.filter((colors) => !ColorGenerator.usedColors.includes(colors));
  }
  /**
   *
   * @param numColors
   */
  static generateColors(numColors: number, usedColors: string[] = []) {
    const colors: string[] = [];
    const MAX_POSSIBLE_HEX_COLOR_VALUE = 10777215;
    const contrastThreshold = 4.5;

    // Loop until we have generated the desired number of colors
    while (colors.length < numColors) {
      // Generate a random color
      const color = `#${Math.floor(Math.random() * MAX_POSSIBLE_HEX_COLOR_VALUE).toString(16)}`;
      // Check if the color is already used
      if (!usedColors.includes(color)) {
        // Check the color contrast with existing colors
        let goodContrast = true;
        if (ColorGenerator.getContrastRatio(color, '#FFFFFF') < contrastThreshold) {
          goodContrast = false;
        }

        if (goodContrast && ColorGenerator.isValidHexColor(color)) {
          colors.push(color);
        }
      }
    }

    ColorGenerator.availableColors = colors;
  }
  /**
   * helper function to determine if the passed string is a valid color
   * @param color
   * @returns
   */
  static isValidHexColor(color: string): boolean {
    const hexRegex = /^#([A-F0-9]{6}|[A-F0-9]{3})$/i;
    return hexRegex.test(color);
  }
  /**
   * the function calculates the contrast ratio between the two colors using the relative luminance values.
   * The contrast ratio is defined as the ratio between the luminance of the lighter color and the luminance of the darker color, plus a constant offset (1 by default).
   * The formula used in this function adds the offset to both luminance values, takes the maximum and minimum of the two resulting values, and calculates the ratio between them. The resulting contrast ratio is returned as a single number.
   * @param color1
   * @param color2
   * @returns
   */
  static getContrastRatio(color1: string, color2: string): number {
    // Convert hex colors to RGB values
    const [r1, g1, b1] = ColorGenerator.hexToRgb(color1);
    const [r2, g2, b2] = ColorGenerator.hexToRgb(color2);

    // Calculate the luminance of each color
    const lum1 = ColorGenerator.getRelativeLuminance(r1, g1, b1);
    const lum2 = ColorGenerator.getRelativeLuminance(r2, g2, b2);

    // Calculate the contrast ratio
    return (Math.max(lum1, lum2) + ColorGenerator.CONTRAST_OFFSET) / (Math.min(lum1, lum2) + ColorGenerator.CONTRAST_OFFSET);
  }
  /**
   * For example, hex.substring(1, 3) extracts the substring starting at index 1 and ending at index 3 (exclusive) from the input hex string
   * 16 for hexadecimal
   * @param hex
   * @returns
   */
  static hexToRgb(hex: string): [number, number, number] {
    const r = Number.parseInt(hex.substring(1, 3), 16);
    const g = Number.parseInt(hex.substring(3, 5), 16);
    const b = Number.parseInt(hex.substring(5, 7), 16);
    return [r, g, b];
  }

  /**
   * Calculate the relative luminance of an RGB color
   * The saturation value is calculated based on the individual color component and the maximum color value.
   * If the saturation value is less than or equal to the saturation threshold, a simple calculation is performed to determine the saturation value.
   * Otherwise, a more complex calculation is performed using the contrast offset and other constants to determine the saturation value.
   * Finally, the function calculates the relative luminance of the RGB color by multiplying each of the saturation values by their corresponding color offsets and adding them together.
   * The result is returned as a single number representing the relative luminance of the RGB color.
   * @param red
   * @param green
   * @param blue
   * @returns
   */
  static getRelativeLuminance(red: number, green: number, blue: number): number {
    const MAX = 255;
    const sat_threshold = 0.04;
    const red_offset = 0.2;
    const green_offset = 0.7;
    const blue_offset = 0.0722;
    const sat_count = 12.92;
    const contrastDivider = 1.055;
    const multi = 2.4;

    const [red_saturation, green_saturation, blue_saturation] = [red, green, blue].map((color) => {
      const saturation_rgb = color / MAX;
      return saturation_rgb <= sat_threshold ? saturation_rgb / sat_count : ((saturation_rgb + ColorGenerator.CONTRAST_OFFSET) / contrastDivider) ** multi;
    });
    return red_offset * red_saturation + green_offset * green_saturation + blue_offset * blue_saturation;
  }
}
